import Vue from "vue";
import axios from "axios";
import store from "@/store";
import * as authService from "@/services/auth.service";
import router from "@/router";

export { HTTP, authHTTP, TOKEN_HEADER_STRING };

// Configure the API URL in the .env.[production/development] files =)
const API_URL = process.env.VUE_APP_API_URL;
const TOKEN_HEADER_STRING = "JWT ";

// Axios instance for unauthenticated access
const HTTP = axios.create({
  baseURL: API_URL
});
// Add basic headers
HTTP.defaults.headers.common["Content-Type"] = "application/json";
HTTP.defaults.headers.common["Accept"] = "application/json";

// Axios instance for authenticated access
const authHTTP = axios.create({
  baseURL: API_URL
});
// Add basic headers
authHTTP.defaults.headers.common["Content-Type"] = "application/json";
authHTTP.defaults.headers.common["Accept"] = "application/json";

Vue.prototype.$http = HTTP;
Vue.prototype.$authHttp = authHTTP;

/* Interceptor to persist the session using the refresh tokens.
 *
 * This was based on the excellent example at:
 *
 * https://www.techynovice.com/setting-up-JWT-token-refresh-mechanism-with-axios/
 *
 * Whilst this is *usually* handled by the Vue router instance (because it's
 * called in the `beforeEach` route guard and therefore better handles
 * navigating back to a cached/saved path), the interceptor is necessary to
 * manage cases where the API is called when the route isn't being changed.
 *
 * For example, if the access token has expired after the route was loaded but
 * before a form is submitted then this interceptor will capture and manage
 * that case.
 *
 * The logic is as follows:
 *
 * 1. The interceptor looks for 401 (unauthorised) responses
 *
 * 2. It then calls `refreshTokenRequest()` to try to refresh the access token
 *
 * 3. This function:
 *
 *    - Caches the original request in `subscribers`
 *
 *    - Calls the auth.service to refresh the token.
 *
 *    - On success, update the store and axios config with the new token and then
 *      calls the `onAccessTokenFetched()` function to fire off new requests
 *      for all of the subscribers.
 *
 * 4. If the refresh fails then the auth.store is used to logout the user and
 * they're re-directed back to the login page.
 */

// List of URL for token endpoints NOT handled by the interceptor
const token_urls = [
  "auth/jwt/create/",
  "auth/jwt/refresh/",
  "auth/jwt/refresh/cookie/"
];

function tokenIsExpiredError(response) {
  return response.status == 401 && !token_urls.includes(response.config.url);
}

// Interceptor to handle access JWT expired (401)
authHTTP.interceptors.response.use(
  response => {
    // If the request succeeds, we don't have to do anything and just return the response
    return response;
  },
  error => {
    if (tokenIsExpiredError(error.response)) {
      return refreshTokenRequest(error);
    } else {
      // If the error is due to other reasons, throw it back to axios
      return Promise.reject(error);
    }
  }
);

let doingRefresh = false;
let subscribers = []; // Requests that will re-try after refresh

function addSubscriber(callback) {
  subscribers.push(callback);
}

function onAccessTokenFetched(access_token) {
  // When the refresh is successful, start retrying the requests one by one and
  // empty the queue
  subscribers.forEach(callback => callback(access_token));
  subscribers = [];
}

async function refreshTokenRequest(error) {
  try {
    const { response: errorResponse } = error;
    let has_refresh_cookie = localStorage.getItem("has_refresh_cookie");
    if (has_refresh_cookie == false) {
      // Can't refresh (or not allowed to), so reject the original promise
      return Promise.reject(error);
    }
    // Add the request to the list of subscribers
    const retryOriginalRequest = new Promise(resolve => {
      addSubscriber(access_token => {
        errorResponse.config.headers["Authorization"] =
          TOKEN_HEADER_STRING + " " + access_token;
        resolve(axios(errorResponse.config));
      });
    });

    // If this is the *first* request then try to refresh the access token
    if (!doingRefresh) {
      doingRefresh = true; // <= To defer later requests
      // Refresh the token
      authService
        .refreshToken()
        .then(response => {
          if (!response.data) {
            // Something went horribly wrong, so again just raise the error
            return Promise.reject(error);
          }
          const newToken = response.data.access;
          store.commit("auth/setAccessToken", newToken);
          authHTTP.defaults.headers.common["Authorization"] =
            TOKEN_HEADER_STRING + newToken;
          doingRefresh = false;
          onAccessTokenFetched(newToken);
        })
        .catch(function() {
          doingRefresh = false;
          store.dispatch("auth/logout");
          router.replace("/login");
        });
    }
    return retryOriginalRequest;
  } catch (err) {
    return Promise.reject(err);
  }
}

/* Add credentials to all axios requests */
authHTTP.interceptors.request.use(
  function(config) {
    config.withCredentials = true;
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);
